import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { Actions, Store } from '@ngxs/store';
import { ModuleManagersService } from 'app/library/module-managers/services/module-managers.service';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { ModuleGlobalService } from 'app/library/module/services/module-global.service';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ModuleCategory } from '../../../module/enums/moduleCategory';
import { Module } from '../../../module/models/module.model';
import { SortMyPublishedModules } from '../../../my-published-modules/actions/index';
import { TagMultiSelectService } from '../../../tag/services/multi-select.service';
import { PublishedModuleDataSource } from './published-module-datasource';

@Component({
    selector: 'app-my-published-modules-list-table',
    templateUrl: './my-published-modules-list-table.component.html',
    styleUrls: ['./my-published-modules-list-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyPublishedModulesListTableComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @Input()
    myModule$: Observable<Module[]>;

    @Input()
    searchTerm: string;

    @Output()
    clickedTag = new EventEmitter<MaxBrainTag>();

    @ViewChild(MatSort)
    sort: MatSort;

    profileId = this._store.selectSnapshot(UserState).me;
    dataSource: PublishedModuleDataSource;
    displayedColumns = ['avatar', 'name', 'startDate', 'endDate', 'status'];
    ModuleKind = ModuleKind;

    moduleStatuses = ModuleStatuses;
    /** Bumps after layout/resize so filterTags remeasures with a real cell width. */
    tagsLayoutKey = 0;
    private readonly _resize$ = new Subject<void>();

    @HostListener('window:resize')
    onWindowResize(): void {
        this._resize$.next();
    }

    constructor(
        public tagMultiSelectService: TagMultiSelectService,
        private _action$: Actions,
        private _store: Store,
        private moduleGlobalService: ModuleGlobalService,
        private _moduleManagersService: ModuleManagersService,
        private _cd: ChangeDetectorRef
    ) {
        super();
        this._resize$.pipe(debounceTime(150), takeUntil(this._unsubscribeAll)).subscribe(() => this._bumpTagsLayout());
    }

    ngOnInit(): void {
        this.dataSource = new PublishedModuleDataSource(this.myModule$, this._store, this._action$, this.tagMultiSelectService);

        setTimeout(() => {
            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortMyPublishedModules(sort));

                this.loadUsersPage();
            });
        });
    }

    ngAfterViewInit(): void {
        this._cd.detectChanges();
        setTimeout(() => {
            requestAnimationFrame(() => this._bumpTagsLayout());
        });
    }

    private _bumpTagsLayout(): void {
        this.tagsLayoutKey++;
        this._cd.markForCheck();
    }

    filterByTag(tag: MaxBrainTag): void {
        this.clickedTag.emit(tag);
    }

    loadUsersPage(): void {
        const user = this._store.selectSnapshot(UserState.getEntity);

        this.dataSource.loadModules(
            ModuleCategory.Published,
            this.searchTerm,
            this.sort.active,
            this.sort.direction,
            100,
            1,
            user?.id || this.profileId,
            this.tagMultiSelectService.selectedTags.map((tag) => parseInt(tag.id))
        );
    }

    goToModule(module: Module): void {
        this.moduleGlobalService.setParentUrl(window.location.pathname);
        this._moduleManagersService.goToModule(this.profileId, module, 'overview');
    }

    visualLength(text: string): number {
        const ruler = document.getElementById('ruler-2');
        ruler.innerHTML = text;
        const toReturn = ruler.offsetWidth;
        ruler.innerHTML = '';
        return toReturn;
    }
}
